<!-- src/apps/secret/components/canonical/SecretConfirmationForm.vue -->

<script setup lang="ts">
  import { useI18n } from 'vue-i18n';
  import NeedHelpModal from '@/shared/components/modals/NeedHelpModal.vue';
  import CornerBrackets from '@/shared/components/ui/CornerBrackets.vue';
  import SecretRecipientHelpContent from '@/apps/secret/components/SecretRecipientHelpContent.vue';
  import { useBootstrapStore } from '@/shared/stores/bootstrapStore';
  import type { Secret, SecretDetails } from '@/schemas/shapes/v3/secret';
  import { ref, computed } from 'vue';

  const bootstrapStore = useBootstrapStore();
  const helpEnabled = computed(() => bootstrapStore.ui?.help?.enabled ?? true);

  interface Props {
    secretIdentifier: string;
    record: Secret | null;
    details: SecretDetails | null;
    isSubmitting: boolean;
    error: unknown;
  }

  const props = defineProps<Props>();
  const emit = defineEmits(['user-confirmed']);
  const { t } = useI18n();

  const passphrase = ref('');

  // Generate unique IDs for ARIA attributes based on secretIdentifier
  const formHeadingId = computed(() => `form-heading-${props.secretIdentifier}`);
  const passphraseInputId = computed(() => `passphrase-${props.secretIdentifier}`);
  const passphraseHeadingId = computed(() => `passphrase-heading-${props.secretIdentifier}`);
  const passphraseDescriptionId = computed(() => `passphrase-description-${props.secretIdentifier}`);

  // Determine the primary status message based on record state
  const statusMessage = computed(() => {
    // Secret has passphrase protection
    if (props.record?.has_passphrase) {
      return t('web.shared.requires_passphrase');
    }
    // Verification secret (no passphrase)
    if (props.record?.verification) {
      return t('web.COMMON.click_to_verify');
    }
    // Regular secret without passphrase - just needs confirmation click
    return t('web.COMMON.click_to_continue');
  });

  // Handle form submission
  const submitForm = async () => {
    emit('user-confirmed', passphrase.value);
  };
</script>

<template>
  <!-- Terminal panel (Figma Make reveal chrome) -->
  <!-- prettier-ignore-attribute class -->
  <div
    class="relative w-full border border-day-line bg-day
      dark:border-white/[0.07] dark:bg-night-100"
    role="region"
    :aria-labelledby="formHeadingId">
    <!-- Status strip -->
    <!-- prettier-ignore-attribute class -->
    <div
      data-testid="secret-status-strip"
      class="flex h-9 items-center justify-between gap-4 overflow-x-auto
        border-b border-day-line bg-day-200 px-3.5
        dark:border-white/[0.13] dark:bg-night-200">
      <div class="flex shrink-0 items-center gap-4">
        <!-- prettier-ignore-attribute class -->
        <span
          class="flex items-center gap-1.5 font-mono text-[9px] uppercase tracking-[0.08em]
            text-brand-600 dark:text-brand-500">
          <span
            class="inline-block size-[5px] shrink-0 rounded-full bg-brand-500 shadow-[0_0_6px_var(--color-brand-500)]"
            aria-hidden="true"></span>
          {{ t('reveal_terminal.status_encrypted') }}
        </span>
        <!-- prettier-ignore-attribute class -->
        <span
          class="font-mono text-[9px] uppercase tracking-[0.08em]
            text-day-ink-faint dark:text-night-ink-faint">
          AES-256
        </span>
        <!-- prettier-ignore-attribute class -->
        <span
          class="font-mono text-[9px] uppercase tracking-[0.08em]
            text-day-ink-faint dark:text-night-ink-faint">
          {{ t('reveal_terminal.reads_remaining') }}
        </span>
      </div>
      <!-- prettier-ignore-attribute class -->
      <span
        v-if="record?.has_passphrase"
        class="flex shrink-0 items-center gap-1.5 font-mono text-[9px] uppercase
          tracking-[0.08em] text-emerald-600 dark:text-emerald-400">
        <span
          class="inline-block size-[5px] shrink-0 rounded-full bg-[#00C47A] shadow-[0_0_6px_#00C47A]"
          aria-hidden="true"></span>
        {{ t('reveal_terminal.passphrase_required') }}
      </span>
    </div>

    <div class="p-4 sm:p-6">
      <!-- Hero label -->
      <div class="mb-4 flex items-center gap-2">
        <span
          class="inline-block size-1.5 bg-brand-500 shadow-[0_0_6px_var(--color-brand-500)]"
          aria-hidden="true"></span>
        <!-- prettier-ignore-attribute class -->
        <span
          class="font-mono text-[10px] uppercase tracking-[0.08em]
            text-day-ink-faint dark:text-night-ink-faint">
          {{ t('reveal_terminal.hero_label') }}
        </span>
      </div>

      <!-- Header section with title, status, and help link -->
      <div class="mb-6 flex items-start justify-between">
        <div>
          <h1
            :id="formHeadingId"
            class="font-mono text-xl font-bold tracking-tight text-day-ink dark:text-night-ink">
            {{ statusMessage }}
          </h1>
          <div
            v-if="!record?.has_passphrase"
            class="mt-1.5 text-base text-day-ink-muted dark:text-night-ink-muted"
            role="status"
            aria-live="polite">
            {{ t('web.COMMON.careful_only_see_once') }}
          </div>
        </div>

        <!-- Help Modal Trigger positioned to the right -->
        <NeedHelpModal
          v-if="helpEnabled"
          link-icon-name="question-mark-circle-16-solid"
          link-text-label="">
          <!-- prettier-ignore-attribute class -->
          <button
            type="button"
            class="ml-4 text-sm font-medium
              text-brand-600 hover:text-brand-700
              focus:underline focus:outline-none dark:text-brand-500 dark:hover:text-brand-400"
            data-testid="secret-help-modal-trigger">
            {{ t('web.COMMON.need_help') }}?
          </button>
          <template #content>
            <SecretRecipientHelpContent />
          </template>
        </NeedHelpModal>
      </div>

      <form
        @submit.prevent="submitForm"
        class="space-y-6"
        :aria-labelledby="record?.has_passphrase ? passphraseHeadingId : undefined"
        :aria-describedby="record?.has_passphrase ? passphraseDescriptionId : undefined"
        data-testid="secret-confirmation-form">
        <!-- Hidden username field for accessibility/password managers -->
        <input
          type="text"
          name="username"
          autocomplete="username"
          class="sr-only"
          tabindex="-1"
          aria-hidden="true" />

        <!-- Conditional Passphrase Section -->
        <div
          v-if="record?.has_passphrase"
          class="space-y-2">
          <p
            :id="passphraseDescriptionId"
            class="font-mono text-[10px] leading-relaxed text-day-ink-faint dark:text-night-ink-faint">
            {{ t('reveal_terminal.passphrase_hint') }}
          </p>
          <div>
            <label
              :for="passphraseInputId"
              class="sr-only">
              {{ t('web.COMMON.enter_passphrase_here') }}
            </label>
            <!-- prettier-ignore-attribute class -->
            <input
              v-model="passphrase"
              :id="passphraseInputId"
              type="password"
              name="passphrase"
              class="w-full border border-day-line-2 bg-day px-4 py-3
                font-mono text-base text-day-ink transition-colors
                placeholder:text-gray-400
                focus:border-brand-500 focus:outline-none focus:ring-2 focus:ring-brand-500
                dark:border-white/[0.13] dark:bg-night dark:text-night-ink
                dark:placeholder:text-night-ink-faint dark:focus:border-brand-400"
              autocomplete="current-password"
              :placeholder="t('web.COMMON.enter_passphrase_here')"
              aria-required="true"
              :aria-invalid="error ? 'true' : undefined"
              :aria-errormessage="error ? 'passphrase-error' : undefined"
              :aria-describedby="passphraseDescriptionId"
              data-testid="secret-reveal-passphrase-input" />
          </div>
          <p
            v-if="error"
            id="passphrase-error"
            class="mt-1 text-sm text-red-600 dark:text-red-400"
            role="alert"
            data-testid="secret-reveal-error">
            {{ String(error) }}
          </p>
        </div>

        <!-- Submission Button -->
        <!-- prettier-ignore-attribute class -->
        <button
          type="submit"
          :disabled="isSubmitting"
          class="w-full bg-brand-600 px-6 py-3.5
            font-mono text-sm font-semibold uppercase tracking-[0.08em] text-white
            transition-colors duration-150 ease-in-out hover:bg-brand-700
            focus:outline-none focus:ring-2 focus:ring-brand-500 focus:ring-offset-2
            disabled:cursor-not-allowed disabled:opacity-50
            dark:focus:ring-offset-night-100"
          data-testid="secret-reveal-submit">
          {{ isSubmitting ? t('web.COMMON.submitting') : t('web.COMMON.click_to_continue') }}
        </button>
      </form>

      <!-- Single-view note (honest copy: the backend destroys on read) -->
      <p class="mt-4 font-mono text-[10px] leading-relaxed text-day-ink-faint dark:text-night-ink-faint">
        {{ t('reveal_terminal.reveal_note') }}
      </p>
    </div>

    <CornerBrackets />
  </div>
</template>
